
import React from 'react';
import { SpeakabilityBreakdown as SpeakabilityBreakdownType } from '@/utils/speakabilityCalculator';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';

interface SpeakabilityBreakdownProps {
  breakdown: SpeakabilityBreakdownType;
  className?: string;
}

const SpeakabilityBreakdown: React.FC<SpeakabilityBreakdownProps> = ({ breakdown, className }) => {
  const getProgressColor = (score: number, max: number) => {
    const percent = (score / max) * 100;
    if (percent >= 85) return 'bg-green-500';
    if (percent >= 70) return 'bg-blue-500';
    if (percent >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const categories = [
    { 
      name: 'Fluency', 
      value: breakdown.fluency,
      max: 40,
      description: 'Measures smoothness, flow, and ease of articulation',
      details: breakdown.fluencyEarned || [
        '+10 if different start/end consonants',
        '+10 if total syllables between 1 and 3',
        '+10 if no word ends with a hard-to-pronounce consonant',
        '+5 if natural vowel clusters',
        '+5 if common phoneme patterns'
      ]
    },
    { 
      name: 'Familiarity', 
      value: breakdown.familiarity,
      max: 30,
      description: 'Based on phonetic patterns that feel intuitive',
      details: breakdown.familiarityEarned || [
        '+10 for balanced consonant-vowel patterns',
        '+10 for optimal vowel-consonant ratio (35-65%)',
        '+5 for ending with a vowel sound',
        '+5 for using standard characters only'
      ]
    },
    { 
      name: 'Simplicity', 
      value: breakdown.simplicity,
      max: 30,
      description: 'Focuses on clear pronunciation and spelling',
      details: breakdown.simplicityEarned || [
        '+10 if each word has 1-3 syllables',
        '+10 if no confusing double letters',
        '+5 if no ambiguous sound patterns',
        '+5 if consistent capitalization'
      ]
    }
  ];

  // Only show bonus category if there are bonus points
  if (breakdown.bonus && breakdown.bonus > 0) {
    categories.push({
      name: 'Bonus',
      value: breakdown.bonus,
      max: 10,
      description: 'Extra points for memorable characteristics',
      details: breakdown.bonusEarned || [
        '+5 for palindromic patterns',
        '+5 for balanced length/syllable ratio',
        '+5 for short, clean domain names'
      ]
    });
  }

  return (
    <div className={cn("space-y-4", className)}>
      <h3 className="text-lg font-medium mb-3">Speakability Breakdown</h3>
      
      {breakdown.words && breakdown.words.length > 0 && (
        <div className="mb-4">
          <div className="text-sm text-gray-700 mb-1">Words detected:</div>
          <div className="flex flex-wrap gap-2">
            {breakdown.words.map((word, i) => (
              <Badge key={i} variant="outline" className="text-sm">
                {word}
              </Badge>
            ))}
          </div>
        </div>
      )}
      
      <div className="space-y-4">
        {categories.map((category) => (
          <div key={category.name} className="space-y-1">
            <div className="flex justify-between">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-sm font-medium flex items-center gap-1 cursor-help">
                      {category.name}
                      <span className="text-xs text-gray-400">(0-{category.max})</span>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <div className="space-y-2">
                      <p className="font-medium">{category.name} Score</p>
                      <ul className="text-xs space-y-1 list-disc pl-4">
                        {category.details.map((detail, i) => (
                          <li key={i}>{detail}</li>
                        ))}
                      </ul>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <span className="text-sm font-medium">{category.value}/{category.max}</span>
            </div>
            <Progress 
              value={(category.value / category.max) * 100} 
              className={cn("h-2", getProgressColor(category.value, category.max))}
            />
            <p className="text-xs text-gray-500">{category.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SpeakabilityBreakdown;
